import React, { useMemo } from 'react';
import {
    DataGridPro,
    GridColDef,
    GridColumnGroupingModel,
    GridRenderCellParams,
} from '@mui/x-data-grid-pro';
import { Box, LinearProgress, Tooltip, Typography } from '@mui/material';
import { formatCurrencyTwoToThreeDecimals } from '../Utilities/numberFormatter';
import { formatDate } from '../Utilities/dateUtility';
import { ColumnResizeParams, ColumnWidths } from '../Utilities/columnWidthUtils';
import {
    FiscalMonthColumn,
    getFiscalMonthColumns,
    MonthlyCogsCell,
    ProductCogsInterval,
    resolveCogsForMonth,
} from '../Utilities/productCogsMonthly';

export type ProductCogsMonthlyRow = {
    id: string;
    productId: number;
    skuNumber: string;
    skuDescription: string;
    groupName?: string | null;
    startDate?: string | Date | null;
    endDate?: string | Date | null;
    cogs: ProductCogsInterval[];
};

type ProductCogsMonthlyViewProps = {
    rows: ProductCogsMonthlyRow[];
    startMonth: number;
    fiscalYear: number;
    loading: boolean;
    selectedRowId?: string | null;
    onRowSelectionModelChange: (selectionModel: string[]) => void;
    toolbar: React.ComponentType<any>;
    toolbarProps: Record<string, unknown>;
    columnWidths: ColumnWidths;
    onColumnResize: (params: ColumnResizeParams) => void;
};

const formatConflictTooltip = (entries: ProductCogsInterval[]): React.ReactNode => (
    <Box>
        {entries.map((entry, index) => {
            const endLabel = entry.endDate ? ` – ${formatDate(entry.endDate)}` : '';
            return (
                <Typography key={`${entry.startDate}-${index}`} variant="body2">
                    {formatCurrencyTwoToThreeDecimals(entry.unitCogs)} ({formatDate(entry.startDate)}{endLabel})
                </Typography>
            );
        })}
    </Box>
);

const renderMonthCell = (params: GridRenderCellParams): React.ReactNode => {
    const cell = (params.row.monthlyCogs as Record<string, MonthlyCogsCell> | undefined)?.[params.field];
    if (!cell || cell.value == null) {
        return '';
    }

    const formatted = formatCurrencyTwoToThreeDecimals(cell.value);
    if (!cell.hasConflict) {
        return formatted;
    }

    return (
        <Tooltip title={formatConflictTooltip(cell.entries)}>
            <span>{formatted}*</span>
        </Tooltip>
    );
};

const buildMonthColumn = (column: FiscalMonthColumn): GridColDef => ({
    field: column.field,
    headerName: column.label,
    width: 110,
    sortable: true,
    align: 'center',
    headerAlign: 'center',
    valueGetter: (_value: unknown, row: { monthlyCogs?: Record<string, MonthlyCogsCell> }) =>
        row.monthlyCogs?.[column.field]?.value ?? null,
    renderCell: renderMonthCell,
});

const ProductCogsMonthlyView: React.FC<ProductCogsMonthlyViewProps> = ({
    rows,
    startMonth,
    fiscalYear,
    loading,
    selectedRowId,
    onRowSelectionModelChange,
    toolbar: Toolbar,
    toolbarProps,
    columnWidths,
    onColumnResize,
}) => {
    const monthColumns = useMemo(
        () => getFiscalMonthColumns(startMonth, fiscalYear),
        [startMonth, fiscalYear]
    );

    const gridRows = useMemo(
        () =>
            rows.map((row) => {
                const monthValues: Record<string, MonthlyCogsCell> = {};
                for (const column of monthColumns) {
                    monthValues[column.field] = resolveCogsForMonth(
                        row.cogs,
                        column.year,
                        column.month,
                        row
                    );
                }
                return { ...row, monthlyCogs: monthValues };
            }),
        [rows, monthColumns]
    );

    const columns = useMemo((): GridColDef[] => {
        const identityColumns: GridColDef[] = [
            { field: 'skuNumber', headerName: 'SKU', width: columnWidths.skuNumber ?? 100 },
            { field: 'skuDescription', headerName: 'Description', width: columnWidths.skuDescription ?? 200, minWidth: 120 },
            { field: 'groupName', headerName: 'Group', width: columnWidths.groupName ?? 150 },
        ];

        return [...identityColumns, ...monthColumns.map(buildMonthColumn)];
    }, [monthColumns, columnWidths]);

    const columnGroupingModel = useMemo((): GridColumnGroupingModel => [
        {
            groupId: 'unitCogs',
            headerName: 'Total Unit COGs',
            headerAlign: 'center',
            children: monthColumns.map((column) => ({ field: column.field })),
        },
    ], [monthColumns]);

    return (
        <Box sx={{ height: '100%', width: '100%' }} data-testid="product-cogs-monthly-view">
            <DataGridPro
                rows={gridRows}
                columns={columns}
                getRowId={(row) => row.id}
                loading={loading}
                pagination
                columnGroupingModel={columnGroupingModel}
                rowSelectionModel={selectedRowId ? [selectedRowId] : []}
                onRowSelectionModelChange={(selectionModel) =>
                    onRowSelectionModelChange(selectionModel as string[])
                }
                onColumnResize={onColumnResize}
                initialState={{
                    pinnedColumns: { left: ['skuNumber', 'skuDescription', 'groupName'] },
                }}
                slots={{
                    toolbar: Toolbar,
                    loadingOverlay: LinearProgress as any,
                }}
                slotProps={{
                    toolbar: toolbarProps as any,
                }}
                sx={{
                    '& .MuiDataGrid-columnHeader--filledGroup .MuiDataGrid-columnHeaderTitleContainer': {
                        justifyContent: 'center',
                    },
                    '& .MuiDataGrid-columnHeader--filledGroup .MuiDataGrid-columnHeaderTitle': {
                        width: '100%',
                        textAlign: 'center',
                    },
                }}
            />
        </Box>
    );
};

export default ProductCogsMonthlyView;
